import { useEffect, useState } from "react";
import * as Yup from "yup";
import dayjs from "dayjs";
import { Avatar, Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { Form, Formik } from "formik";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { gridSpacing } from "../../../store/constants";
import SelectWrapper from "../../../utils/ui-components/FormsUI/Select";
import { useTheme } from "@mui/material/styles";
import { BulkSmsCampaignCreationValues } from ".";
import RadioGroupWrapper from "../../../utils/ui-components/FormsUI/RadioGroup";
import DateTimePickerWrapper from "../../../utils/ui-components/FormsUI/DateTimePicker";
import { hasUnicode, messageCounterUpdate } from "../../../utils/utils";
import SuffixTextareaWrapper from "../../../utils/ui-components/FormsUI/SuffixTextarea";
import SimCardIcon from "@mui/icons-material/SimCard";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

// Load the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

function BulkSmsCampaignCreateAd(props: {
  role: number;
  initialState: BulkSmsCampaignCreationValues;
  isLoadingMask: boolean;
  getChannelAndMaskList: (id: number | string) => void;
  setInitialState: (x: any) => void;
  setOpetorValues: (x: any) => void;
  isTimeDisabled: Function;
  channelList: {
    value: number | string;
    label: string;
    0: { unicode: number; non_unicode: number };
    1: { unicode: number; non_unicode: number };
    dialogRate: number;
    nonDialogRate: number;
  }[];
  maskList: { value: number | string; label: string; type: number };
  blackOutTime: {
    startTime: string;
    endTime: string;
  };
  optOutTemplate: {
    dialog: string;
    nonDialog: string;
  };
  handleBack: () => void;
  handleNext: () => void;
  suffix: string;
}) {
  const [messageLength, setmessageLength] = useState<{
    unicode: number;
    non_unicode: number;
  }>({ unicode: 0, non_unicode: 0 });
  const theme: any = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    messageCount(props.initialState);
  }, []);

  const messageCount = (values: BulkSmsCampaignCreationValues) => {
    let messageLength = {
      unicode: 0,
      non_unicode: 0,
    };
    if (values.mask && values.channel) {
      let channel = props.channelList.find((channel) => channel.value === values.channel)!;
      let maskType = props.maskList?.type;
      if (maskType === 0) {
        messageLength = channel?.[0];
      } else {
        messageLength = channel?.[1];
      }
    }
    setmessageLength(messageLength);
  };

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
        <Box
          sx={{
            mt: 5,
            padding: "15px",
          }}
        >
          <Formik
            initialValues={{ ...props.initialState }}
            enableReinitialize
            // validateOnMount
            validationSchema={Yup.object().shape({
              channel: Yup.string().test("required", "Please select channel", function (value: any) {
                return Number(props.initialState.numberList.numberListType) === 1 ? !!value : true;
              }),
              scheduleNow: Yup.boolean().notRequired(),
              startTime: Yup.object()
                .nullable()
                .required("Please enter start time")
                .when("start_time", {
                  is: (val: any) => !val,
                  then: (schema) =>
                    schema
                      .test("MIN_DATE", "Date Time must be after current time.", function (value: any) {
                        const { scheduleNow } = this.parent;
                        return Number(scheduleNow) === 1 ? true : new Date(value).getTime() > new Date().getTime();
                      })
                      .test(
                        "blackoutTime",
                        `Choose a time outside ${
                          dayjs(props.blackOutTime.startTime, "HH:mm:ss").format("hh:mm:ssa") +
                          " to " +
                          dayjs(props.blackOutTime.endTime, "HH:mm:ss").format("hh:mm:ssa")
                        } blackout period.`,
                        (value: any) => {
                          const selectedTime = value.format("HH:mm:ss");
                          const [startHour, startMinute, startSecond] = props.blackOutTime.startTime.split(":");
                          const [endHour, endMinute, endSecond] = props.blackOutTime.endTime.split(":");

                          const blackoutStart = new Date(selectedTime);
                          blackoutStart.setHours(Number(startHour), Number(startMinute), Number(startSecond), 0);

                          const blackoutEnd = new Date(selectedTime);
                          blackoutEnd.setHours(Number(endHour), Number(endMinute), Number(endSecond), 0);

                          if (props.blackOutTime.startTime > props.blackOutTime.endTime) {
                            return !(selectedTime < props.blackOutTime.endTime || selectedTime > props.blackOutTime.startTime);
                          } else {
                            return !(selectedTime >= props.blackOutTime.startTime && selectedTime <= props.blackOutTime.endTime);
                          }
                        }
                      ),
                }),
              endTime: Yup.object()
                .nullable()
                .required("Please enter end time")
                .test("AFTER_START", "End Time must be after Start Time.", function (value: any) {
                  const { startTime } = this.parent; // Accessing other fields in the schema
                  return value && startTime && new Date(value).getTime() > new Date(startTime).getTime();
                }),
              message: Yup.string()
                .test("required", "Please enter message", function (value: any) {
                  const { numberList } = this.parent;
                  return Number(numberList.numberListType) === 1 ? !!value : true;
                })
                .test("max_length", "Channel selection is required before proceeding", function (value: any) {
                  const { channel, numberList } = this.parent;
                  if (channel) {
                    return true;
                  } else {
                    return Number(numberList.numberListType) === 1 ? false : true;
                  }
                })
                .test("max_length", "Message exceed maximun characters", function (value: any) {
                  const { channel, numberList } = this.parent;
                  if (Number(numberList.numberListType) === 1) {
                    if (channel) {
                      if (messageLength.non_unicode === 0 && messageLength.unicode === 0) {
                        return false;
                      } else if (value?.replace(props.suffix, "").trim().length) {
                        if (hasUnicode(value)) {
                          return value?.replace(props.suffix, "").trim().length <= messageLength.unicode;
                        } else {
                          return value?.replace(props.suffix, "").trim().length <= messageLength.non_unicode;
                        }
                      } else {
                        return false;
                      }
                    } else {
                      return false;
                    }
                  } else return true;
                })
                .max(1000, "Message must contain below 1000 characters"),
              // .required("Please enter Message"),
            })}
            onSubmit={(values: BulkSmsCampaignCreationValues) => {
              props.setInitialState(values);
              props.handleNext();
            }}
          >
            {({ values, isValid, setFieldValue }) => {
              if (Number(values.scheduleNow) === 1 && (values.startTime < dayjs() || values.startTime > dayjs().add(10, "seconds"))) {
                setFieldValue("startTime", dayjs().add(10, "second"));
              }
              if (values.endTime < values.startTime) {
                setFieldValue("endTime", dayjs(values.startTime).add(10, "minute"));
              }

              return (
                <Form>
                  <Grid container rowSpacing={2} columnSpacing={gridSpacing}>
                    <Grid item xl={9} lg={8} md={7.5} xs={12}>
                      <Grid container rowSpacing={2} columnSpacing={gridSpacing}>
                        <Grid item lg={6} md={12} xs={12}>
                          <RadioGroupWrapper
                            required
                            name="medium"
                            label="Medium"
                            radioGroup={[
                              {
                                value: 1,
                                label: "SMS",
                              },
                              {
                                value: 2,
                                label: "Real Time",
                              },
                            ]}
                            disabled={true}
                          />
                        </Grid>
                        {Number(props.initialState.numberList.numberListType) === 1 && (
                          <Grid item lg={6} md={12} sm={12} xs={12}>
                            <SelectWrapper
                              required
                              name="channel"
                              label="Channels"
                              options={props.channelList}
                              customHandleChange={(e: any) => {
                                const channelData = props.channelList.find((item) => item.value === e.target.value);
                                props.setOpetorValues({
                                  onnet: channelData?.dialogRate,
                                  offnet: channelData?.nonDialogRate,
                                });
                                setFieldValue("channel", e.target.value);
                                messageCount({
                                  ...values,
                                  channel: e.target.value,
                                });
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item lg={12} md={12} xs={12}>
                          <RadioGroupWrapper
                            required
                            name="scheduleNow"
                            label="Campaign Start Type"
                            radioGroup={[
                              {
                                value: 1,
                                label: "Start Now",
                              },
                              {
                                value: 0,
                                label: "Schedule",
                              },
                            ]}
                            disabled={false}
                          />
                        </Grid>
                        <Grid item xl={4} lg={6} md={8} sm={6} xs={12}>
                          <DateTimePickerWrapper
                            required
                            name="startTime"
                            label={"Start Date Time *"}
                            disablePast
                            disabled={Number(values.scheduleNow) === 1}
                          />
                        </Grid>
                        <Grid item xl={4} lg={6} md={8} sm={6} xs={12}>
                          <DateTimePickerWrapper required name="endTime" label={"End Date Time *"} disablePast />
                        </Grid>
                        {Number(props.initialState.numberList.numberListType) === 1 && (
                          <Grid item xs={12}>
                            <SuffixTextareaWrapper
                              disabled={false}
                              required
                              type={"text"}
                              suffix={props.suffix}
                              maskType={props.maskList?.type}
                              name="message"
                              label="Message"
                              multiline={true}
                              rows={values.message.split(/\r\n|\r|\n/).length < 5 ? 5 : values.message.split(/\r\n|\r|\n/).length}
                            />

                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: values.channel ? "space-between" : "end",
                                mb: 1,
                              }}
                            >
                              {values.channel && (
                                <p style={{ color: "gray" }}>
                                  {hasUnicode(values.message) ? `Unicode message` : `Non unicode message`} {" ( Total character count : "}
                                  {hasUnicode(values.message) ? messageLength.unicode : messageLength.non_unicode} {" )"}
                                </p>
                              )}
                              <p style={{ color: "gray" }}>
                                Remainig Characters:{" "}
                                {values.mask && values.channel
                                  ? messageCounterUpdate(values.message.replace(props.suffix, "").trim(), messageLength)
                                  : "0"}
                              </p>
                            </Box>
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "start",
                              }}
                            >
                              <p style={{ color: "gray" }}>
                                <b>Note:</b> Unicode message length might be different from character length
                              </p>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    {!matchDownSM && (
                      <Grid item xl={3} lg={4} md={4.5} xs={12} container justifyContent="center">
                        <Box
                          style={{
                            backgroundImage: `url(${require(theme.palette.mode === "light"
                              ? "../../../assets/images/ada_light_phone_background.PNG"
                              : "../../../assets/images/ada_dark_phone_background.PNG")})`,
                            width: "250px",
                            height: "540px",
                            overflowY: "auto",
                            backgroundSize: "cover",
                            borderRadius: 15,
                            border: `1px solid ${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.secondary.main}`,
                          }}
                        >
                          <Typography
                            variant="body1"
                            style={{
                              fontSize: 15,
                              fontWeight: "bold",
                              marginTop: props.maskList ? 35 : 75,

                              marginBottom: 20,
                              marginLeft: 35,
                              color: theme.palette.mode === "light" ? "black" : "white",
                            }}
                          >
                            {props.maskList?.label ?? ""}
                          </Typography>
                          <Grid
                            container
                            style={{
                              maxHeight: "100%",
                              overflowY: "auto",
                              padding: 10,
                            }}
                          >
                            <Avatar sx={{ width: 20, height: 20, marginTop: 0.5 }}></Avatar>
                            <Box
                              style={{
                                width: "200px",
                                marginLeft: 5,
                                borderRadius: "0 20px 20px 20px",
                                minHeight: 30,
                                backgroundColor: theme.palette.mode === "light" ? "#efefef" : "#1f242b",
                              }}
                            >
                              {values.message ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: (Number(values.numberList.numberListType) === 1
                                      ? values.message
                                      : `${values.message}\n${
                                          Number(props.maskList.type) === 0 ? "" : props.optOutTemplate.dialog.replace("#_MASK", props.maskList.label)
                                        }`
                                    ).replace(/\r\n|\r|\n/g, "<br/>"),
                                  }}
                                  style={{
                                    padding: 10,
                                    inlineSize: "100%",
                                    overflowWrap: "break-word",
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Grid>
                          {/* {Number(props.maskList.type) === 1 ? (
                          <Grid
                            container
                            style={{
                              maxHeight: "100%",
                              overflowY: "auto",
                              padding: 10,
                            }}
                          >
                            <Avatar sx={{ width: 20, height: 20, marginTop: 0.5 }}></Avatar>
                            <Box
                              style={{
                                width: "200px",
                                marginLeft: 5,
                                borderRadius: "0 20px 20px 20px",
                                backgroundColor: theme.palette.mode === "light" ? "#efefef" : "#1f242b",
                                minHeight: 30,
                              }}
                            >
                              {values.message ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: values.message
                                      .replace(
                                        props.suffix,
                                        props.suffix === ""
                                          ? ""
                                          : `\n${props.optOutTemplate.nonDialog.replace("#_MASK", props.maskList?.label ?? "")}`
                                      )
                                      .replace("\n", "<br/>"),
                                  }}
                                  style={{
                                    padding: 10,
                                    inlineSize: "100%",
                                    overflowWrap: "break-word",
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Grid>
                        ) : (
                          <></>
                        )} */}
                        </Box>
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 2,
                      }}
                    >
                      <Button color="primary" variant="contained" type="button" onClick={props.handleBack} startIcon={<NavigateBeforeIcon />}>
                        Back
                      </Button>
                      {/* <Box sx={{ width: 10 }} /> */}
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        // onClick={() => props.setInitialState(values)}
                        endIcon={<NavigateNextIcon />}
                        disabled={!isValid}
                      >
                        Next
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Grid>
    </Grid>
  );
}

export default BulkSmsCampaignCreateAd;
